import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { Vendor } from "../types/vendor";

export function useVendorProfile() {
    const queryClient = useQueryClient();
    const [userId, setUserId] = useState<string | null>(null);

    // Listen for auth changes so the query auto-refreshes on login/logout
    useEffect(() => {
        const fetchUser = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            setUserId(session?.user?.id ?? null);
        };
        fetchUser();

        const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
            setUserId(session?.user?.id ?? null);
            queryClient.invalidateQueries({ queryKey: ["vendor-profile"] });
        });

        return () => {
            listener?.subscription.unsubscribe();
        };
    }, [queryClient]);

    const fetchVendorProfile = async (): Promise<Vendor | null> => {
        if (!userId) return null;
        console.log("[useVendorProfile] Current user ID:", userId);

        // lightweight existence check (excluding deleted vendors)
        const { data: allVendors } = await supabase
            .from("vendors")
            .select("id")
            .eq("user_id", userId)
            .neq("status", "deleted")
            .not("is_deleted", "eq", true)
            .limit(1);
        if (!allVendors?.length) return null;

        // fetch most recent (excluding deleted vendors)
        const { data, error } = await supabase
            .from("vendors")
            .select("*")
            .eq("user_id", userId)
            .neq("status", "deleted")
            .not("is_deleted", "eq", true)
            .order("created_at", { ascending: false })
            .limit(1);

        if (error || !data?.length) return null;
        return data[0];
    };

    return useQuery<Vendor | null>({
        queryKey: ["vendor-profile", userId],
        queryFn: fetchVendorProfile,
        enabled: !!userId,
        // Prevent refetch on focus to avoid flash when navigating back
        refetchOnWindowFocus: false,
        // Keep data fresh for 5 minutes
        staleTime: 5 * 60 * 1000,
    });
}